import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import MoveFolderPicker from './components/Menu/MoveFolderPicker';
import { isInvalidMoveTarget, normalizeParentId } from './helpers';

export const useMoveItem = ({
  parentId,
  data,
  itemsCache,
  itemsTotals,
  updateReportParent,
  getItemsByParentId,
  getReports,
  currentPage,
  itemsPerPage,
  setCurrentPage,
}) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [item, setItem] = useState(null);
  const [selectedParentId, setSelectedParentId] = useState(undefined);

  const getItemParentId = useCallback((targetItem) => {
    if (data.some((entry) => entry.id === targetItem.id)) {
      return normalizeParentId(parentId);
    }

    return normalizeParentId(targetItem.parentId);
  }, [data, parentId]);

  const refreshAfterMove = useCallback((fromParentId, targetParentId) => {
    const controller = new AbortController();

    Promise.all([
      getItemsByParentId(fromParentId, controller, 1, undefined, false),
      getItemsByParentId(targetParentId, controller, 1, undefined, false),
    ]);

    if (normalizeParentId(parentId) === fromParentId) {
      getReports({ parentId, page: currentPage + 1, perPage: itemsPerPage });
    } else if (normalizeParentId(parentId) === targetParentId) {
      setCurrentPage(0);
      getReports({ parentId: targetParentId || undefined, page: 1, perPage: itemsPerPage });
    }
  }, [parentId, getItemsByParentId, getReports, currentPage, itemsPerPage, setCurrentPage]);

  const moveItem = useCallback((targetItem, targetParentId, onSuccess) => {
    if (!targetItem?.id || targetParentId === undefined) {
      return;
    }

    const fromParentId = getItemParentId(targetItem);
    const normalizedTarget = normalizeParentId(targetParentId);

    if (fromParentId === normalizedTarget) {
      return;
    }

    if (isInvalidMoveTarget(targetItem.id, targetItem.type, normalizedTarget, itemsCache)) {
      return;
    }

    updateReportParent(targetItem.id, normalizedTarget, () => {
      refreshAfterMove(fromParentId, normalizedTarget);
      onSuccess?.();
    });
  }, [getItemParentId, itemsCache, updateReportParent, refreshAfterMove]);

  const openMoveModal = useCallback((targetItem) => {
    setItem({ ...targetItem, parentId: getItemParentId(targetItem) });
    setSelectedParentId(undefined);
    setModalOpen(true);
  }, [getItemParentId]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setItem(null);
    setSelectedParentId(undefined);
  }, []);

  const handleDragEnd = useCallback((itemId, targetFolderId) => {
    if (!itemId || targetFolderId === undefined) {
      return;
    }

    const targetItem = data.find((entry) => entry.id === itemId);
    moveItem(targetItem || { id: itemId }, targetFolderId);
  }, [data, moveItem]);

  useEffect(() => {
    if (!modalOpen || itemsCache.root) {
      return undefined;
    }

    const controller = new AbortController();
    getItemsByParentId(null, controller, 1, undefined, false);
    return () => controller.abort();
  }, [modalOpen, getItemsByParentId, itemsCache]);

  const modal = (
    <Modal isOpen={modalOpen} toggle={closeModal}>
      <ModalHeader toggle={closeModal}>{t('move-to-folder')}</ModalHeader>
      <ModalBody>
        {item && (
          <p className="mb-3">
            <strong>{item.name}</strong>
          </p>
        )}
        <MoveFolderPicker
          selectedParentId={selectedParentId}
          onSelect={setSelectedParentId}
          movingItemId={item?.type === 'folder' ? item.id : null}
          currentParentId={item?.parentId ?? null}
          getItemsByParentId={getItemsByParentId}
          itemsCache={itemsCache}
          itemsTotals={itemsTotals}
        />
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          onClick={() => {
            if (item && selectedParentId !== undefined) {
              moveItem(item, selectedParentId, closeModal);
            }
          }}
          disabled={selectedParentId === undefined}
        >
          {t('move')}
        </Button>
        <Button color="secondary" onClick={closeModal}>
          {t('cancel')}
        </Button>
      </ModalFooter>
    </Modal>
  );

  return { openMoveModal, handleDragEnd, modal };
};
